import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell, ChevronLeft, Play, Plus, Save, Send, Square, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "participants", label: "Participants" },
  { key: "templates", label: "Templates" },
];

export default function CycleDetail() {
  const { cycleId } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [cycle, setCycle] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const [c, dash, p, camp, us] = await Promise.all([
        api.get(`/lift/cycles/${cycleId}`),
        api.get(`/lift/cycles/${cycleId}/dashboard`),
        api.get(`/lift/cycles/${cycleId}/participants`),
        api.get("/campuses"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setCycle(c.data);
      setDashboard(dash.data);
      setParticipants(p.data);
      setCampuses(camp.data);
      setUsers(us.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cycleId]);

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/lift/cycles/${cycleId}/send-reminders`);
      toast.success(`Sent ${data.sent} reminder${data.sent === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const activate = async () => {
    if (!window.confirm("Activate this cycle? Participants will be notified.")) return;
    await api.post(`/lift/cycles/${cycleId}/activate`);
    toast.success("Cycle activated.");
    load();
  };
  const close = async () => {
    if (!window.confirm("Close this cycle?")) return;
    await api.post(`/lift/cycles/${cycleId}/close`);
    toast.success("Cycle closed.");
    load();
  };

  if (loading || !cycle || !dashboard) return <p className="italic text-charcoal-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="lift-cycle-detail">
      <Link to="/lift/cycles" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Cycles
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <ModuleTitleLIFT />
          <h1 className="mt-3 text-4xl font-serif text-forest-900">{cycle.name}</h1>
          <p className="text-sm text-charcoal-muted">
            {cycle.period_start} → {cycle.period_end} · deadline {cycle.deadline} · status{" "}
            <span className="patria-chip-green">{cycle.status}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {cycle.status === "active" && (
            <button className="patria-btn-secondary" onClick={sendReminders} disabled={sending} data-testid="cycle-send-reminders">
              <Bell className="h-4 w-4" strokeWidth={1.5} /> {sending ? "Sending..." : "Send reminders now"}
            </button>
          )}
          {cycle.status === "draft" && (
            <button className="patria-btn-primary" onClick={activate} data-testid="cycle-activate"><Play className="h-4 w-4" strokeWidth={1.5} /> Activate</button>
          )}
          {cycle.status === "active" && (
            <button className="patria-btn-terracotta" onClick={close} data-testid="cycle-close"><Square className="h-4 w-4" strokeWidth={1.5} /> Close</button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
              tab === t.key ? "bg-forest-600 text-cream-100" : "bg-cream-100 border border-sage-300 text-charcoal hover:bg-sage-200/60"
            }`}
            data-testid={`cycle-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab totals={dashboard.totals} by_campus={dashboard.by_campus} />}
      {tab === "participants" && (
        <ParticipantsTab
          cycleId={cycleId}
          participants={participants}
          campuses={campuses}
          users={users}
          isActive={cycle.status === "active"}
          onReload={load}
        />
      )}
      {tab === "templates" && (
        <TemplatesTab cycle={cycle} isSuperAdmin={isSuperAdmin} onReload={load} />
      )}
    </div>
  );
}

/* ------------------- Dashboard tab ------------------- */
function DashboardTab({ totals, by_campus }) {
  return (
    <div className="space-y-5" data-testid="cycle-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Stat label="Participants" value={totals.participants} accent="green" />
        <Stat label="Self submitted" value={totals.self_submitted} accent="green" />
        <Stat label="Manager submitted" value={totals.manager_submitted} accent="green" />
        <Stat label="Peer requests" value={totals.peer_requests} accent="green" />
        <Stat label="Peer submitted" value={totals.peer_submitted} accent="green" />
        <Stat label="Redaction pending" value={totals.redaction_pending} accent="terra" />
        <Stat label="Finalized" value={totals.synthesized} accent="green" />
        <Stat label="Overdue" value={totals.overdue} accent="terra" />
      </div>
      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr><th>Campus</th><th>Participants</th><th>Self submitted</th><th>Manager submitted</th></tr>
          </thead>
          <tbody>
            {by_campus.map((c) => (
              <tr key={c.campus}>
                <td>{c.campus}</td>
                <td>{c.total}</td>
                <td>{c.self_submitted}</td>
                <td>{c.manager_submitted}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "green" }) {
  return (
    <div className="patria-card p-4">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className={`mt-1 text-3xl font-serif ${accent === "terra" ? "text-terracotta-600" : "text-forest-900"}`}>{value}</p>
    </div>
  );
}

/* ------------------- Participants tab ------------------- */
function ParticipantsTab({ cycleId, participants, campuses, users, isActive, onReload }) {
  const [mode, setMode] = useState("all");
  const [campusId, setCampusId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [q, setQ] = useState("");
  const [assigning, setAssigning] = useState(false);

  const assign = async () => {
    setAssigning(true);
    try {
      const body = { mode };
      if (mode === "campus") body.campus_id = campusId;
      if (mode === "employees") body.employee_ids = selectedIds;
      const { data } = await api.post(`/lift/cycles/${cycleId}/assign`, body);
      toast.success(`Assigned ${data.count} participant${data.count === 1 ? "" : "s"}.`);
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = q.toLowerCase();
    return users.filter((u) => !u.archived && (!query || (u.name + u.email).toLowerCase().includes(query))).slice(0, 60);
  }, [users, q]);

  return (
    <div className="space-y-5" data-testid="cycle-participants-tab">
      <div className="patria-card p-5 space-y-4">
        <h3 className="text-xl font-serif text-forest-900">Assign Participants</h3>
        <div className="flex flex-wrap gap-3 items-center">
          {["all", "campus", "employees"].map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <input type="radio" checked={mode === m} onChange={() => setMode(m)} data-testid={`assign-mode-${m}`} />
              {m === "all" ? "All active staff" : m === "campus" ? "By campus" : "Hand-selected"}
            </label>
          ))}
        </div>
        {mode === "campus" && (
          <select className="patria-input" value={campusId} onChange={(e) => setCampusId(e.target.value)} data-testid="assign-campus">
            <option value="">Select a campus</option>
            {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {mode === "employees" && (
          <div>
            <input className="patria-input w-full mb-3" placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} />
            <ul className="max-h-64 overflow-y-auto divide-y divide-sage-300/60">
              {filteredUsers.map((u) => (
                <li key={u.id} className="py-2 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(u.id)}
                    onChange={() => setSelectedIds((s) => s.includes(u.id) ? s.filter((x) => x !== u.id) : [...s, u.id])}
                    data-testid={`assign-pick-${u.id}`}
                  />
                  <div>
                    <p className="font-medium text-forest-900">{u.name}</p>
                    <p className="text-xs text-charcoal-muted">{u.email}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button className="patria-btn-primary" onClick={assign} disabled={assigning} data-testid="assign-submit">
          <Send className="h-4 w-4" strokeWidth={1.5} />
          {assigning ? "Assigning..." : "Assign"}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Campus</th>
              <th>Self</th>
              <th>Manager eval</th>
              <th>Synthesis</th>
              <th>Ack</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.id} data-testid={`participant-row-${p.employee_id}`}>
                <td className="font-medium text-forest-900">
                  {p.employee_name}
                  <p className="text-xs text-charcoal-muted">{p.employee_email}</p>
                </td>
                <td>{campuses.find((c) => c.id === p.campus_id)?.name || "—"}</td>
                <td>
                  <span className={p.status_self_reflection === "submitted" ? "patria-chip-green" : "patria-chip-neutral"}>
                    {p.status_self_reflection?.replace("_", " ")}
                  </span>
                </td>
                <td>
                  <span className={p.status_manager_evaluation === "submitted" ? "patria-chip-green" : "patria-chip-neutral"}>
                    {p.status_manager_evaluation?.replace("_", " ")}
                  </span>
                </td>
                <td className="capitalize">{p.status_synthesis?.replace("_", " ")}</td>
                <td>{p.employee_ack_at ? "✅" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------- Templates tab ------------------- */
function TemplatesTab({ cycle, isSuperAdmin, onReload }) {
  const [templates, setTemplates] = useState(cycle.templates);
  const [saving, setSaving] = useState(false);
  useEffect(() => setTemplates(cycle.templates), [cycle]);

  const editable = isSuperAdmin;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/lift/cycles/${cycle.id}`, { templates });
      toast.success("Templates saved.");
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const groups = ["self_reflection", "manager_evaluation", "peer_feedback"];

  const updateQ = (group, idx, patch) => {
    setTemplates((t) => ({
      ...t,
      [group]: t[group].map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  };
  const addQ = (group) => {
    setTemplates((t) => ({
      ...t,
      [group]: [...t[group], { id: `q-${Date.now()}`, text: "", type: "long_text", order: t[group].length + 1 }],
    }));
  };
  const removeQ = (group, idx) => {
    setTemplates((t) => ({ ...t, [group]: t[group].filter((_, i) => i !== idx) }));
  };

  if (!editable) {
    return (
      <div className="patria-card p-6">
        <p className="text-sm text-charcoal-soft">
          Only <strong>Super Admin</strong> can edit LIFT question templates. Ask your Super Admin for changes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cycle-templates-tab">
      {groups.map((g) => (
        <div key={g} className="patria-card p-5">
          <h3 className="text-xl font-serif text-forest-900 mb-3 capitalize">{g.replace("_", " ")}</h3>
          <ul className="space-y-3">
            {templates[g].map((q, idx) => (
              <li key={q.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start" data-testid={`tmpl-${g}-${idx}`}>
                <input
                  className="patria-input sm:col-span-6"
                  value={q.text}
                  onChange={(e) => updateQ(g, idx, { text: e.target.value })}
                />
                <select
                  className="patria-input sm:col-span-2"
                  value={q.type}
                  onChange={(e) => updateQ(g, idx, { type: e.target.value })}
                >
                  <option value="long_text">Long text</option>
                  <option value="rating_5">Rating 1–5</option>
                </select>
                <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(g, idx, { required: e.target.checked })} />
                  Required
                </label>
                <input
                  type="number"
                  className="patria-input sm:col-span-1"
                  value={q.order || idx + 1}
                  onChange={(e) => updateQ(g, idx, { order: parseInt(e.target.value || "0", 10) })}
                />
                <button className="patria-btn-ghost text-terracotta-600 sm:col-span-1" onClick={() => removeQ(g, idx)}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>
          <button className="patria-btn-secondary mt-3" onClick={() => addQ(g)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add question
          </button>
        </div>
      ))}
      <button className="patria-btn-primary" onClick={save} disabled={saving} data-testid="tmpl-save-btn">
        <Save className="h-4 w-4" strokeWidth={1.5} /> {saving ? "Saving..." : "Save templates"}
      </button>
    </div>
  );
}
